/**
 * Pure builder for the create-content payload the wizard sends to
 * `startContentCreation`. Extracted from ContentCreationWizard so every
 * scenario's payload shaping (from-text vs generate vs from-existing, voice,
 * book format, audio/book auto-flags) is unit-testable — this is what guarantees
 * each wizard variant produces the CORRECT workflow inputs.
 */

import type { WizardScenario } from '../types/enums';
import type { WizardMode, CreationMode } from './wizardScenario';
import type { FromTextMode } from './storyPackageWorkflow';
import { clampPageCount, minutesForBlocks } from '@/contexts/stories/utils/storyShape';

const HERO_MODES: FromTextMode[] = ['recast_hero', 'based_on', 'incorporate'];

export interface WizardCreateInput {
  scenario: WizardScenario;
  wizardMode: WizardMode;
  parameters: {
    creationMode: CreationMode;
    title?: string;
    text?: string;
    fromTextMode?: FromTextMode;
    fromTextHeroName?: string;
    fromTextReplacements?: Array<{ from: string; to: string }>;
    fromTextChangeVector?: string;
    fromTextBasedOnMode?: 'retell' | 'reimagine';
    fromTextTosAccepted?: boolean;
    fromTextInjectHeroes?: string[];
    audienceChildName?: string;
    prompt: string;
    genre: string;
    theme?: string;
    setting?: string;
    energy?: number;
    pageCount?: number;
    desiredDurationMinutes?: number;
    language: string;
    ageGroup: string;
    characterIds: string[];
    characterAssignments: { characterId: string; role: string }[];
    /** User consented to publish publicly (gallery/YouTube + text reuse) → 5% rebate. */
    publicConsent?: boolean;
    /** From-text: public story whose text seeds this one (based_on reuse). */
    basisStoryId?: string;
    /** "Hero from a photo" drafts — processed into characters on the worker. */
    pendingPhotoHeroes?: Array<{ name: string; photoKey: string }>;
    /** Heroes from a photo, created at upload (their ids are in characterIds). */
    photoHeroes?: Array<{ characterId: string }>;
    /** The wizard's parse of the source + whom each hero replaces (S1 before the order). */
    preParsed?: unknown;
    replaceMap?: Array<{ entityId: string; characterId: string }>;
    /** «Спросить родителя» answered with «add heroes». */
    heroesAddConfirmed?: boolean;
  };
  wantAudio: boolean;
  wantIllustrations: boolean;
  voiceSettings: {
    voiceId?: string;
    voiceName?: string;
    stability?: number;
    similarityBoost?: number;
  };
  illustrationStyle: string;
  illustrationFormat: string;
  audioOptions: { autoGenerateBlocks: boolean; autoAssemble: boolean };
  illustrationOptions: { autoGeneratePortraits: boolean; autoGenerateImages: boolean };
  /** Exports of the order (pipeline v2): PDF, print-ready file, video. */
  exportOptions?: ExportOptions;
  targetLanguage: string;
  /**
   * Translate the TEXT only — do not reproduce the original's book and
   * narration in the new language. The default is to reproduce them: a
   * translation of an illustrated, narrated story is the same story, so it
   * grows the same shapes (`matchTranslationToSource`). A parent who only
   * wants to read the words in another language says so here, and the
   * follow-up never runs.
   */
  translateTextOnly?: boolean;
  sourceStoryId: string | null;
}

export interface ExportOptions {
  pdf: boolean;
  print: boolean;
  video: boolean;
}

/** PDF comes with every book (made by code, free); print and video are asked for. */
export const DEFAULT_EXPORT_OPTIONS: ExportOptions = { pdf: true, print: false, video: false };

export interface WizardCreatePayload {
  scenario: WizardScenario;
  sourceEntityId?: string;
  params: Record<string, unknown>;
}

export function buildCreateParams(input: WizardCreateInput): WizardCreatePayload {
  const {
    scenario, wizardMode, parameters: p, wantAudio, wantIllustrations,
    voiceSettings, illustrationStyle, illustrationFormat, audioOptions, illustrationOptions,
    targetLanguage, translateTextOnly, sourceStoryId, exportOptions,
  } = input;

  const isFromExistingStory = wizardMode === 'extend' || wizardMode === 'translate';
  const isFromText = p.creationMode === 'from-text' && wizardMode === 'new';
  const needsVoice = wantAudio;
  const mode = p.fromTextMode ?? 'verbatim';

  const params: Record<string, unknown> = {
    ...(isFromText
      ? {
          title: p.title,
          text: p.text,
          fromTextMode: mode,
          ...(HERO_MODES.includes(mode)
            ? { fromTextHeroName: (p.fromTextHeroName ?? p.audienceChildName)?.trim() || undefined }
            : {}),
          // Hero swaps apply in recast_hero AND based_on (the basis cast-swap:
          // "по мотивам, but replace these heroes with mine").
          ...((mode === 'recast_hero' || mode === 'based_on') && p.fromTextReplacements?.length
            ? { fromTextReplacements: p.fromTextReplacements }
            : {}),
          ...(mode === 'based_on' && p.fromTextChangeVector?.trim()
            ? { fromTextChangeVector: p.fromTextChangeVector.trim() }
            : {}),
          ...(mode === 'based_on' && p.fromTextBasedOnMode
            ? { fromTextBasedOnMode: p.fromTextBasedOnMode }
            : {}),
          ...(mode === 'incorporate' && p.fromTextInjectHeroes?.length
            ? { fromTextInjectHeroes: p.fromTextInjectHeroes }
            : {}),
        }
      : {
          prompt: p.prompt,
          storyType: p.genre,
          ...(p.theme ? { theme: p.theme } : {}),
          ...(p.setting ? { setting: p.setting } : {}),
          ...(p.energy != null ? { energy: p.energy } : {}),
          // PAGES, explicitly. The executor prefers an explicit blockCount over
          // any duration estimate, and the writer is told exactly this number
          // of blocks — so the length the parent chose is the length produced.
          ...(p.pageCount != null ? { blockCount: clampPageCount(p.pageCount, p.ageGroup) } : {}),
          // Minutes follow from the pages, for the parts of the pipeline that
          // still speak in them (the audio plan, saved roads).
          ...(p.pageCount != null
            ? { desiredDurationMinutes: minutesForBlocks(clampPageCount(p.pageCount, p.ageGroup), p.ageGroup, p.language) }
            : p.desiredDurationMinutes != null
              ? { desiredDurationMinutes: p.desiredDurationMinutes }
              : {}),
        }),
    language: wizardMode === 'translate' ? targetLanguage : p.language,
    ageGroup: p.ageGroup,
    // translate: the heroes are the source's — a pick never travels (the model drops it)
    ...(p.characterIds.length > 0 && !(isFromText && mode === 'translate')
      ? {
          characterIds: p.characterIds,
          characterAssignments: p.characterAssignments.map((a) => ({ characterId: a.characterId, role: a.role })),
        }
      : {}),
    ...(needsVoice
      ? {
          voiceSettings: {
            voiceId: voiceSettings.voiceId,
            voiceName: voiceSettings.voiceName,
            stability: voiceSettings.stability,
            similarityBoost: voiceSettings.similarityBoost,
          },
        }
      : {}),
    ...(wantIllustrations
      ? {
          illustrationStyle,
          illustrationFormat,
          autoGenerateCharacterPortraits: illustrationOptions.autoGeneratePortraits,
          autoGeneratePageImages: illustrationOptions.autoGenerateImages,
        }
      : {}),
    ...(wantAudio
      ? {
          autoGenerateAudioBlocks: audioOptions.autoGenerateBlocks,
          autoAssembleAudio: audioOptions.autoAssemble,
        }
      : {}),
    ...(wizardMode === 'translate' && translateTextOnly ? { translateTextOnly: true } : {}),
    ...(p.publicConsent ? { publicConsent: true } : {}),
    // Paste affirmation travels with the payload — it was a UI-only gate.
    ...(isFromText && p.fromTextTosAccepted ? { textTosAccepted: true } : {}),
    ...(isFromText && p.basisStoryId ? { basisStoryId: p.basisStoryId } : {}),
    ...(p.photoHeroes?.length && !(isFromText && mode === 'translate')
      ? { photoHeroIds: p.photoHeroes.map((h) => h.characterId).filter((id) => p.characterIds.includes(id)) }
      : {}),
    // a pasted text carries its parse; a basis' parse is stored server-side (basis_texts)
    ...(isFromText && p.replaceMap?.length
      ? { replaceMap: p.replaceMap, ...(p.preParsed ? { preParsed: p.preParsed } : {}) }
      : {}),
    ...(p.heroesAddConfirmed ? { heroesAddConfirmed: true } : {}),
    ...(wantIllustrations && exportOptions
      ? { exports: { pdf: exportOptions.pdf, print: exportOptions.print, video: wantAudio && exportOptions.video } }
      : {}),
    ...(p.pendingPhotoHeroes?.length
      ? { pendingPhotoHeroes: p.pendingPhotoHeroes.map((h) => ({ name: h.name, photoKey: h.photoKey })) }
      : {}),
  };

  return {
    scenario,
    ...(isFromExistingStory && sourceStoryId ? { sourceEntityId: sourceStoryId } : {}),
    params,
  };
}
